import { api } from './client';
import type { ItineraryDay, Activity, Venue } from '../types';

export type NextDayUpdate = { dayId: string; originActivity: Activity };
// `dayActivities` is the day's full non-origin activity list, re-sorted
// chronologically, and is only present when adding the activity actually
// changed the order (a timed activity that slotted before existing ones).
// `venue` is the place the server added to the trip's Places list because this
// stop pinned one that wasn't saved yet (null when it was already there).
export type CreateActivityResult = {
  activity: Activity;
  nextDayUpdate: NextDayUpdate | null;
  dayActivities?: Activity[] | null;
  venue?: Venue | null;
};
// `activities` is the day as the server actually stored it. The client re-deals
// times locally for the optimistic update, but that is a guess — reconcile with
// this on the response so the two implementations can't stay out of step.
export type ReorderResult = { nextDayUpdate: NextDayUpdate | null; activities: Activity[] };
// A batch move or duplicate changes two days at once (and can rebuild the
// "continues from" card of the days after them), so the server answers with the
// affected days whole rather than with the rows that moved — the source day's
// remaining order isn't derivable from the moved ids alone.
export type ActivityBatchResult = ItineraryDay[];

export const itineraryApi = {
  listDays: (tripId: string) =>
    api.get<ItineraryDay[]>(`/trips/${tripId}/itinerary`).then(r => r.data),
  createDay: (tripId: string, data: Partial<ItineraryDay>) =>
    api.post<ItineraryDay>(`/trips/${tripId}/itinerary/days`, data).then(r => r.data),
  updateDay: (tripId: string, dayId: string, data: Partial<ItineraryDay>) =>
    api.patch<ItineraryDay>(`/trips/${tripId}/itinerary/days/${dayId}`, data).then(r => r.data),
  deleteDay: (tripId: string, dayId: string) =>
    api.delete(`/trips/${tripId}/itinerary/days/${dayId}`),

  createActivity: (tripId: string, dayId: string, data: Partial<Activity>) =>
    api.post<CreateActivityResult>(`/trips/${tripId}/itinerary/days/${dayId}/activities`, data).then(r => r.data),
  updateActivity: (tripId: string, dayId: string, activityId: string, data: Partial<Activity>) =>
    api.patch<Activity>(`/trips/${tripId}/itinerary/days/${dayId}/activities/${activityId}`, data).then(r => r.data),
  /** ADR-0024: `withMeal` only when the user said the linked meal goes too. */
  deleteActivity: (tripId: string, dayId: string, activityId: string, withMeal = false) =>
    api.delete(
      `/trips/${tripId}/itinerary/days/${dayId}/activities/${activityId}`,
      withMeal ? { params: { withMeal: 'true' } } : undefined,
    ),
  reorderActivities: (tripId: string, dayId: string, orderedIds: string[]) =>
    api.patch<ReorderResult>(`/trips/${tripId}/itinerary/days/${dayId}/activities/reorder`, { orderedIds }).then(r => r.data),

  // Trip-level, not day-level: a batch crosses days, so it has no owning day.
  moveActivities: (tripId: string, activityIds: string[], targetDayId: string) =>
    api.post<ActivityBatchResult>(`/trips/${tripId}/itinerary/activities/move`, { activityIds, targetDayId }).then(r => r.data),
  duplicateActivities: (tripId: string, activityIds: string[], targetDayId: string) =>
    api.post<ActivityBatchResult>(`/trips/${tripId}/itinerary/activities/duplicate`, { activityIds, targetDayId }).then(r => r.data),
};
